import React, { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Loader2, Camera, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const AuthModal = ({ isOpen, onClose }) => {
    const { t } = useLanguage();
    const { signIn, signUp, signInWithGoogle, error, setError } = useAuth();

    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (isLogin) {
                await signIn(email, password);
            } else {
                await signUp(email, password, displayName);
            }
            onClose();
        } catch (err) {
            // Error is already set in AuthContext
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError(null);

        try {
            await signInWithGoogle();
            onClose();
        } catch (err) {
            // Error is already set in AuthContext
        } finally {
            setLoading(false);
        }
    };

    const switchMode = () => {
        setIsLogin(!isLogin);
        setError(null);
        setEmail('');
        setPassword('');
        setDisplayName('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-fadeIn">
            <div className="relative w-full max-w-md liquid-glass-dark overflow-hidden shadow-2xl border border-white/10 animate-scaleIn flex flex-col">
                {/* Header */}
                <div className="relative p-8 text-center">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-2xl liquid-glass border-white/10 hover:bg-white/20 transition-all z-20"
                        aria-label={t('auth.close') || 'Close'}
                    >
                        <X className="w-4 h-4 text-white" />
                    </button>

                    <div className="w-16 h-16 mx-auto mb-6 liquid-glass flex items-center justify-center border-white/10">
                        <Camera className="w-8 h-8 text-green-400" />
                    </div>

                    <h2 className="text-3xl font-extrabold text-white mb-2 tracking-tight">
                        {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
                    </h2>
                    <p className="text-gray-400 font-medium">
                        {isLogin ? t('auth.loginDesc') : t('auth.signupDesc')}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                    {/* Display Name (only for signup) */}
                    {!isLogin && (
                        <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder={t('auth.displayName')}
                                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30 transition-all font-medium"
                                required={!isLogin}
                                aria-label={t('auth.displayName') || 'Display Name'}
                                autoComplete="name"
                            />
                        </div>
                    )}

                    {/* Email */}
                    <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('auth.email')}
                            className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30 transition-all font-medium"
                            required
                            aria-label={t('auth.email') || 'Email'}
                            autoComplete="email"
                        />
                    </div>

                    {/* Password */}
                    <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('auth.password')}
                            className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30 transition-all font-medium"
                            required
                            minLength={6}
                            aria-label={t('auth.password') || 'Password'}
                            autoComplete={isLogin ? 'current-password' : 'new-password'}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                            aria-label={showPassword ? (t('auth.hidePassword') || 'Hide password') : (t('auth.showPassword') || 'Show password')}
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
                            <p className="text-red-400 text-sm text-center font-medium">{error}</p>
                        </div>
                    )}

                    {/* Submit button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-liquid w-full py-4 text-lg flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-6 h-6 animate-spin" />
                                {t('auth.loading')}
                            </>
                        ) : (
                            isLogin ? t('auth.login') : t('auth.signup')
                        )}
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-4 py-2">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">{t('auth.or')}</span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    {/* Google Sign In */}
                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    >
                        <svg className="w-6 h-6" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        {t('auth.continueWithGoogle')}
                    </button>

                    {/* Switch mode */}
                    <p className="text-center text-gray-400 text-sm">
                        {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}
                        <button
                            type="button"
                            onClick={switchMode}
                            className="ml-2 text-green-400 hover:text-green-300 font-medium transition-colors"
                        >
                            {isLogin ? t('auth.signup') : t('auth.login')}
                        </button>
                    </p>
                </form>

                {/* Features highlight */}
                <div className="px-6 pb-6">
                    <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            <span className="text-white text-sm font-medium">{t('auth.benefits')}</span>
                        </div>
                        <ul className="text-gray-400 text-xs space-y-1">
                            <li>• {t('auth.benefit1')}</li>
                            <li>• {t('auth.benefit2')}</li>
                            <li>• {t('auth.benefit3')}</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
